"use client";

import { useState, useRef, useEffect } from "react";
import {
  ArrowRight,
  Menu,
  User,
  X,
  Bell,
  ChevronDown,
  LogOut as LogOutIcon,
  Settings,
  BookMarked,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { HoverLink } from "../../ui/HoverLink";
import { Link, useLocation } from "react-router-dom";
import logo from "../../assets/home/home.svg";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../apiInstances/axiosInstance";
import TopAds from "../Adds/TopAds";
import { LogoutConfirmModal } from "../../authentication/LogOut";

const topLinks = [
  { name: "Find Agents", path: "/find-agents?type=agent" },
  { name: "For Sale", path: "/" },
  { name: "For Agencies", path: "/find-agents?type=agency" },
];
const mainLinks = [
  { name: "Rent", path: "/rent" },
  { name: "Buy", path: "/buy" },
  { name: "New Homes", path: "/new-homes" },
];

function getDisplayName() {
  const email = localStorage.getItem("email");
  if (email && typeof email === "string") {
    const part = email.split("@")[0];
    return part
      ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
      : "User";
  }
  return "User";
}

function getInitials(name) {
  if (!name || name === "User") return "U";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export default function NavBarHomeScreen() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const checkAuth = localStorage.getItem("Token");
  const navigate = useNavigate();
  const displayName = getDisplayName();
  const initials = getInitials(displayName);
  const [ads, setAds] = useState([]);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { longitude, latitude } = pos.coords;
      const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
      axiosInstance(url)
        .then((data) => {
          const state = data.data.address.state.split(' ')[0]
          getAdsList(state)
        })
        .catch((err) =>
          console.log("err", err))
    })
  }, [])

  const getAdsList = (state) => {
    axiosInstance.get(`/advertise/ads/list?address=${state}`)
      .then((res) => {
        const result = res.data.data;
        setAds(result)
      })
      .catch((error) => console.log("err --->", error))
  }

  useEffect(() => {
    function handleClickOutside(e) {
      if (profileRef.current && !profileRef.current.contains(e.target))
        setProfileOpen(false);
    }
    if (profileOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [profileOpen]);
  const getPath = useLocation().pathname.split("/")?.[1];
  return (
    <>
      <header className="z-50 bg-white">
        {getPath == "" || getPath == "advertise" || getPath == "dashboard"
          || getPath == "my-saved-property" || getPath == "SaveSearch"
          || getPath == "collections" || getPath == "account-settings" || getPath == "renter-profile"
          || getPath == "rental-applications" ? "" : (
          <div className='border-b'>
            <div className='max-w-6xl mx-auto '>
              {ads?.topAds && ads?.topAds[0] && <div className="bg-white justify-center w-full  py-4 hidden md:flex ">
                <TopAds list={ads?.topAds} />
              </div>}
            </div>
          </div>)}
        <div className="hidden lg:block bg-gray-100 text-sm">
          <div className="container mx-auto h-10 flex items-center justify-between">
            <div className="flex gap-4 md:gap-6">
              {topLinks.map((item) => (
                <Link to={item.path} key={item.name}>
                  <HoverLink key={item.name} label={item.name} />
                </Link>
              ))}
            </div>
            {checkAuth != undefined || checkAuth != null ? (
              <motion.div
                whileHover="hover"
                initial="rest"
                animate="rest"
                variants={{
                  rest: { scale: 1, boxShadow: "0 0 0 rgba(0,0,0,0)" },
                  hover: {
                    scale: 1.08,
                    boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                  },
                }}
                onClick={() => navigate("/dashboard")}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center cursor-pointer"
              >
                <motion.div
                  variants={{
                    rest: { y: 0, color: "#4b5563" },
                    hover: { y: -1, color: "#2563eb" },
                  }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <User className="w-4 h-4" />
                </motion.div>
              </motion.div>
            ) : (
              ""
            )}
          </div>
        </div>

        <div className="container h-14 sm:h-16 flex items-center justify-between">
          <div className="flex justify-content-center items-center gap-6 xl:gap-8">
            <Link to="/" className="flex items-center gap-2 min-w-0">
              <img
                src={logo}
                alt="logo"
                className="h-6 w-6 md:h-8 md:w-8 flex-shrink-0 -mt-2"
              />
              <span className="font-semibold text-base sm:text-lg text-[#000929] truncate">
                My Real Estate
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
              {mainLinks.map((item) => (
                <Link to={item.path} key={item.name}>
                  <HoverLink key={item.name} label={item.name} />
                </Link>
              ))}
            </nav>
          </div>

          <div className="hidden lg:flex items-center gap-2 md:gap-3">
            {checkAuth === undefined || checkAuth === null ? (
              <motion.button
                whileHover={{ backgroundColor: "#f9fafb" }}
                transition={{ duration: 0.2 }}
                onClick={() => navigate("/log-in")}
                className="px-4 md:px-5 py-2.5 md:py-3 rounded-full border border-gray-300 text-sm font-medium text-gray-700 cursor-pointer"
              >
                Sign in
              </motion.button>
            ) : (
              <div className="flex items-center gap-6" ref={profileRef}>
                <button
                  type="button"
                  className="w-10 h-10 rounded-lg bg-[#F3F6F9] flex items-center justify-center text-gray-600 hover:bg-gray-200 transition"
                  aria-label="Notifications"
                >
                  <Bell className="w-5 h-5" strokeWidth={2.5} />
                </button>
                <div className="w-0.5 h-9 bg-gray-200 rounded-lg" />
                <div className="relative">
                  <motion.button
                    type="button"
                    whileHover={{ backgroundColor: "#f9fafb" }}
                    transition={{ duration: 0.2 }}
                    onClick={() => setProfileOpen(!profileOpen)}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg border-[1.5px] border-[#00092933] bg-white text-left min-w-0 shadow-sm"
                  >
                    <span className="w-8 h-8 rounded-full bg-[#097FE6] flex items-center justify-center text-white text-sm font-bold shrink-0">
                      {initials}
                    </span>
                    <span className="text-sm font-medium text-[#000929] truncate max-w-[100px]">
                      {displayName}
                    </span>
                    <ChevronDown
                      strokeWidth={3}
                      className={`w-4 h-4 shrink-0 transition ${profileOpen ? "rotate-180" : ""}`}
                    />
                  </motion.button>
                  <AnimatePresence>
                    {profileOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50"
                      >
                        <Link
                          to="/dashboard"
                          onClick={() => setProfileOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-gray-800 hover:bg-gray-50 text-sm"
                        >
                          <User className="w-4 h-4 text-gray-600 shrink-0" />
                          My Profile
                        </Link>
                        <div className="border-t border-gray-100" />
                        <Link
                          to="/collections"
                          onClick={() => setProfileOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-gray-800 hover:bg-gray-50 text-sm"
                        >
                          <BookMarked className="w-4 h-4 text-gray-600 shrink-0" />
                          Collections
                        </Link>
                        <div className="border-t border-gray-100" />
                        <Link
                          to="/account-settings"
                          onClick={() => setProfileOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-gray-800 hover:bg-gray-50 text-sm"
                        >
                          <Settings className="w-4 h-4 text-gray-600 shrink-0" />
                          Settings
                        </Link>
                        <div className="border-t border-gray-100" />
                        <button
                          type="button"
                          onClick={() => {
                            setProfileOpen(false);
                            setLogoutModalOpen(true);
                          }}
                          className="flex items-center gap-3 px-4 py-2.5 text-gray-800 hover:bg-gray-50 text-sm w-full"
                        >
                          <LogOutIcon className="w-4 h-4 text-gray-600 shrink-0" />
                          Logout
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}
            {
              checkAuth === undefined || checkAuth === null ? (
                <motion.button
                  whileHover="hover"
                  initial="rest"
                  animate="rest"
                  onClick={() => navigate("/sign-up")}
                  className="flex items-center gap-2 px-4 md:px-5 py-2.5 md:py-3 rounded-full bg-[#097FE6] text-white text-sm font-medium shadow-sm cursor-pointer"
                >
                  <span>Start Now</span>
                  <motion.span
                    variants={{
                      rest: { x: 0, opacity: 0.8 },
                      hover: { x: 4, opacity: 1 },
                    }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <ArrowRight className="w-4 h-4 text-white" />
                  </motion.span>
                </motion.button>

              ) : null
            }
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 flex items-center justify-center"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" strokeWidth={2} />
          </button>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black/40 z-[60] lg:hidden"
                onClick={() => setMenuOpen(false)}
                aria-hidden="true"
              />
              <motion.aside
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
                className="fixed top-0 right-0 bottom-0 w-full max-w-sm bg-white shadow-xl z-[60] lg:hidden flex flex-col"
              >
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <span className="font-semibold text-lg text-[#000929]">
                    Menu
                  </span>
                  <button
                    type="button"
                    onClick={() => setMenuOpen(false)}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
                    aria-label="Close menu"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto py-6 px-4">
                  <div className="flex flex-col gap-1 mb-6">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider px-2 mb-2">
                      Explore
                    </p>
                    {topLinks.map((item) => (
                      <Link
                        key={item.name}
                        to={item.path}
                        onClick={() => setMenuOpen(false)}
                        className="py-2 px-2 rounded-lg hover:bg-gray-50 block"
                      >
                        <HoverLink label={item.name} />
                      </Link>
                    ))}
                  </div>
                  <div className="flex flex-col gap-1 mb-6">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider px-2 mb-2">
                      Property
                    </p>
                    {mainLinks.map((item) => (
                      <Link
                        key={item.name}
                        to={item.path}
                        onClick={() => setMenuOpen(false)}
                        className="py-2 px-2 rounded-lg hover:bg-gray-50 block"
                      >
                        <HoverLink label={item.name} />
                      </Link>
                    ))}
                  </div>
                  <div className="border-t border-gray-200 pt-4 flex flex-col gap-2">
                    {checkAuth === undefined || checkAuth === null ? (
                      <button
                        type="button"
                        onClick={() => navigate("/log-in")}
                        className="w-full py-3 px-4 rounded-full border border-gray-300 text-sm font-medium text-gray-700 text-center"
                      >
                        Sign in
                      </button>
                    ) : (
                      <Link
                        to="/dashboard"
                        onClick={() => setMenuOpen(false)}
                        className="w-full py-3 px-4 rounded-full border border-gray-200 text-sm font-medium text-gray-800 text-center flex items-center justify-center gap-2"
                      >
                        <User className="w-4 h-4" />
                        Dashboard
                      </Link>
                    )}
                    <button
                      onClick={() => navigate("/sign-up")}
                      type="button"
                      className="w-full py-3 px-4 rounded-full bg-[#097FE6] text-white text-sm font-medium flex items-center justify-center gap-2"
                    >
                      <span>Start Now</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>
      </header>

      <LogoutConfirmModal
        open={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={() => {
          localStorage.clear();
          setLogoutModalOpen(false);
          navigate("/");
        }}
      />
    </>
  );
}
